import { Link, NavLink, useNavigate } from "react-router-dom";
import { Sparkles, Menu, X, Phone, MessageSquare } from "lucide-react";
import { useState } from "react";
import { PHONE_DISPLAY, PHONE_TEL, PHONE_SMS } from "@/lib/contact";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/book", label: "Book Now" },
  { to: "/my-bookings", label: "My Bookings" },
  { to: "/admin/login", label: "Admin" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-[#0b0f17]/80 border-b border-white/10"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-[#ff6b00] to-[#ff8a2a] flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(255,107,0,0.6)] group-hover:scale-105 transition">
            <Sparkles className="h-5 w-5 text-[#0b0f17]" strokeWidth={2.6} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold uppercase tracking-tight text-slate-100 text-lg">
              CJ's <span className="text-[#ff6b00]">Mobile Detailing</span>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 -mt-0.5">
              Premium · On-site · AU
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}-link`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full font-display uppercase text-sm font-semibold tracking-wider transition ${
                  isActive
                    ? "text-[#ff6b00] bg-[#ff6b00]/10"
                    : "text-slate-300 hover:text-slate-100 hover:bg-white/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={PHONE_TEL}
            data-testid="nav-call-button"
            className="ml-2 inline-flex items-center gap-2 cj-btn-primary px-5 py-2.5 rounded-full text-sm"
          >
            <Phone className="h-4 w-4" /> CALL {PHONE_DISPLAY}
          </a>
          <button
            onClick={() => navigate("/book")}
            data-testid="nav-book-now-button"
            className="ml-1 cj-btn-ghost px-5 py-2.5 rounded-full text-sm"
          >
            BOOK ONLINE
          </button>
        </nav>

        <button
          className="md:hidden text-slate-200 p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#0b0f17]/95 backdrop-blur-xl">
          <div className="px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s/g, "-")}-link`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl font-display uppercase font-semibold tracking-wider transition ${
                    isActive
                      ? "text-[#ff6b00] bg-[#ff6b00]/10"
                      : "text-slate-300 hover:text-slate-100 hover:bg-white/5"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={PHONE_TEL}
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-call-button"
              className="mt-2 cj-btn-primary px-5 py-3 rounded-full inline-flex items-center justify-center gap-2"
            >
              <Phone className="h-4 w-4" /> CALL {PHONE_DISPLAY}
            </a>
            <a
              href={PHONE_SMS}
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-text-button"
              className="px-5 py-3 rounded-full inline-flex items-center justify-center gap-2 font-display uppercase font-semibold tracking-wider text-slate-200 border border-white/15 hover:bg-white/5"
            >
              <MessageSquare className="h-4 w-4" /> Text us
            </a>
            <button
              onClick={() => {
                setOpen(false);
                navigate("/book");
              }}
              data-testid="nav-mobile-book-now-button"
              className="cj-btn-ghost px-5 py-3 rounded-full"
            >
              BOOK ONLINE
            </button>
          </div>
        </div>
      )}
    </header>
    {/* Floating call button lives OUTSIDE the header: header's backdrop-filter creates a containing block for fixed children */}
    <a
      href={PHONE_TEL}
      data-testid="floating-call-button"
      className="md:hidden fixed bottom-5 right-5 z-[60] cj-btn-primary h-14 pl-5 pr-6 rounded-full shadow-[0_12px_30px_-6px_rgba(255,107,0,0.6)] inline-flex items-center gap-2 font-display uppercase font-extrabold tracking-wider"
    >
      <Phone className="h-5 w-5" /> Call CJ
    </a>
    </>
  );
}
